/**
 * The dashboard's locations endpoints, typed — mirrors `content/api.ts` and `support/api.ts`.
 *
 * Two identifiers matter here and neither is derived from the other:
 *
 * - `GET /api/admin/locations` is the existing narrow list every staff role already depends on
 *   (`auth/routes.ts`). This module reads it for the list screen and does not touch its route.
 * - `POST /api/admin/locations`, `GET/PATCH/DELETE /api/admin/locations/:id`, and everything
 *   nested under it are the wide surface this feature adds, `admin`/`manager` for create and
 *   update, `admin` only for delete — see `admin-routes.ts`'s own route table for why delete sits
 *   behind the narrower bar.
 *
 * Terminals (`maquinas`) and OTC logins are always read and written through a `locationId`, never
 * fleet-wide — the API has no route for that, because the tables themselves are location-scoped.
 * YubiKeys are fleet-wide by construction and live in `../yubikeys/api.ts` instead.
 */
import {
  adminLocationListSchema,
  deleteResultSchema,
  locationDetailSchema,
  maquinaListSchema,
  maquinaSchema,
  otcUserListSchema,
  otcUserSchema,
  type AdminLocationList,
  type CreateLocation,
  type CreateMaquina,
  type CreateOtcUser,
  type DeleteResult,
  type LocationDetail,
  type Maquina,
  type MaquinaList,
  type OtcUser,
  type OtcUserList,
  type UpdateLocation,
  type UpdateMaquina,
  type UpdateOtcUser,
} from '@apuestapr/contracts';

import type { ApiClient, ApiResult } from '../api/client.ts';

export const locationsListPath = '/api/admin/locations';
const locationPath = (id: number): string => `${locationsListPath}/${String(id)}`;
const maquinasPath = (locationId: number): string => `${locationPath(locationId)}/maquinas`;
const maquinaPath = (locationId: number, maquinaId: number): string =>
  `${maquinasPath(locationId)}/${String(maquinaId)}`;
const otcUsersPath = (locationId: number): string => `${locationPath(locationId)}/otc-users`;
const otcUserPath = (locationId: number, otcUserId: number): string =>
  `${otcUsersPath(locationId)}/${String(otcUserId)}`;

export interface LocationsApi {
  readonly list: () => Promise<ApiResult<AdminLocationList>>;
  readonly detail: (id: number) => Promise<ApiResult<LocationDetail>>;
  readonly create: (input: CreateLocation, csrfToken: string) => Promise<ApiResult<LocationDetail>>;
  readonly update: (
    id: number,
    input: UpdateLocation,
    csrfToken: string,
  ) => Promise<ApiResult<LocationDetail>>;
  readonly deleteLocation: (id: number, csrfToken: string) => Promise<ApiResult<DeleteResult>>;
  readonly listMaquinas: (locationId: number) => Promise<ApiResult<MaquinaList>>;
  readonly createMaquina: (
    locationId: number,
    input: CreateMaquina,
    csrfToken: string,
  ) => Promise<ApiResult<Maquina>>;
  readonly updateMaquina: (
    locationId: number,
    maquinaId: number,
    input: UpdateMaquina,
    csrfToken: string,
  ) => Promise<ApiResult<Maquina>>;
  readonly deleteMaquina: (
    locationId: number,
    maquinaId: number,
    csrfToken: string,
  ) => Promise<ApiResult<DeleteResult>>;
  readonly listOtcUsers: (locationId: number) => Promise<ApiResult<OtcUserList>>;
  readonly createOtcUser: (
    locationId: number,
    input: CreateOtcUser,
    csrfToken: string,
  ) => Promise<ApiResult<OtcUser>>;
  readonly updateOtcUser: (
    locationId: number,
    otcUserId: number,
    input: UpdateOtcUser,
    csrfToken: string,
  ) => Promise<ApiResult<OtcUser>>;
  readonly deleteOtcUser: (
    locationId: number,
    otcUserId: number,
    csrfToken: string,
  ) => Promise<ApiResult<DeleteResult>>;
}

export const createLocationsApi = (client: ApiClient): LocationsApi => ({
  list: () => client.get(locationsListPath, adminLocationListSchema),
  detail: (id) => client.get(locationPath(id), locationDetailSchema),
  create: (input, csrfToken) =>
    client.send('POST', locationsListPath, locationDetailSchema, { body: input, csrfToken }),
  update: (id, input, csrfToken) =>
    client.send('PATCH', locationPath(id), locationDetailSchema, { body: input, csrfToken }),
  deleteLocation: (id, csrfToken) =>
    client.send('DELETE', locationPath(id), deleteResultSchema, { csrfToken }),
  listMaquinas: (locationId) => client.get(maquinasPath(locationId), maquinaListSchema),
  createMaquina: (locationId, input, csrfToken) =>
    client.send('POST', maquinasPath(locationId), maquinaSchema, { body: input, csrfToken }),
  updateMaquina: (locationId, maquinaId, input, csrfToken) =>
    client.send('PATCH', maquinaPath(locationId, maquinaId), maquinaSchema, {
      body: input,
      csrfToken,
    }),
  deleteMaquina: (locationId, maquinaId, csrfToken) =>
    client.send('DELETE', maquinaPath(locationId, maquinaId), deleteResultSchema, { csrfToken }),
  listOtcUsers: (locationId) => client.get(otcUsersPath(locationId), otcUserListSchema),
  createOtcUser: (locationId, input, csrfToken) =>
    client.send('POST', otcUsersPath(locationId), otcUserSchema, { body: input, csrfToken }),
  updateOtcUser: (locationId, otcUserId, input, csrfToken) =>
    client.send('PATCH', otcUserPath(locationId, otcUserId), otcUserSchema, {
      body: input,
      csrfToken,
    }),
  deleteOtcUser: (locationId, otcUserId, csrfToken) =>
    client.send('DELETE', otcUserPath(locationId, otcUserId), deleteResultSchema, { csrfToken }),
});
